<template>
    <div class="page">
        <IndexHeader />

        <main class="page-main">
            <!-- Hero -->
            <section class="hero">
                <div class="hero-content">
                    <div class="hero-text">
                        <span class="hero-badge">
                            <star-filled />
                            稳定 · 安全 · 高效
                        </span>
                        <h1 class="hero-title">易售SUP 授权系统</h1>
                        <p class="hero-subtitle">
                            面向企业与个人的软件授权平台，支持多版本、多档位和灵活定价，快速交付，持续迭代。
                        </p>
                        <div class="hero-actions">
                            <a href="#pricing" class="btn-primary">
                                <appstore-outlined />
                                立即查看方案
                            </a>
                            <a href="#service" class="btn-ghost" @click.prevent="openNotice">
                                <phone-outlined />
                                查看公告
                            </a>
                        </div>
                        <div class="hero-kpis">
                            <div class="kpi">
                                <div class="kpi-value">99.9%</div>
                                <div class="kpi-label">可用性</div>
                            </div>
                            <div class="kpi">
                                <div class="kpi-value">秒级</div>
                                <div class="kpi-label">开通速度</div>
                            </div>
                            <div class="kpi">
                                <div class="kpi-value">7x24</div>
                                <div class="kpi-label">技术支持</div>
                            </div>
                        </div>
                    </div>

                    <div class="hero-card">
                        <div class="hero-card-head">
                            <h3>授权能力速览</h3>
                            <span class="hero-card-tag">企业级保障</span>
                        </div>
                        <ul class="hero-list">
                            <li><check-circle-filled /> 版本管理与升级策略</li>
                            <li><check-circle-filled /> 价格梯度与授权时长</li>
                            <li><check-circle-filled /> 代理等级与权益分层</li>
                            <li><check-circle-filled /> 统一授权中心管理</li>
                        </ul>
                        <div class="hero-card-footer">
                            <span>10,000+ 用户使用</span>
                            <span class="dot"></span>
                            <span>稳定交付</span>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Featured Pricing -->
            <section id="pricing" class="pricing" v-if="featuredPlugin">
                <div class="pricing-head">
                    <div>
                        <h2 class="section-title">官方授权价格方案</h2>
                        <p class="section-desc">
                            {{ featuredPlugin.name }} ·
                            {{ featuredPlugin.desc || '多版本、多档位授权，稳定交付与灵活定价。' }}
                        </p>
                    </div>
                    <div class="pricing-meta">
                        <span><clock-circle-outlined /> 秒级开通</span>
                        <span><lock-outlined /> 加密授权</span>
                        <span><customer-service-outlined /> 7x24 支持</span>
                    </div>
                </div>

                <div v-if="featuredPluginPrice" class="pricing-body">
                    <div class="version-tabs">
                        <a-tabs v-model:activeKey="activeFeaturedVersion" type="card">
                            <a-tab-pane v-for="(version_data, version_key) in featuredPluginPrice" :key="version_key"
                                :tab="version_data.name" />
                        </a-tabs>
                    </div>

                    <div v-if="featuredPluginPrice[activeFeaturedVersion]?.desc" class="version-desc">
                        <info-circle-outlined />
                        <p v-html="formatDescription(featuredPluginPrice[activeFeaturedVersion].desc)"></p>
                    </div>

                    <div class="price-grid">
                        <div v-for="(item, index) in (featuredPluginPrice[activeFeaturedVersion]?.meals || [])"
                            :key="index" class="price-card">
                            <div class="price-card-head" :class="{ accent: item.grade_conf.grade > 0 }">
                                <div class="price-title">
                                    <span class="grade-name">{{ item.grade_conf.grade_name }}</span>
                                    <span v-if="item.grade_conf.grade === 0" class="grade-badge">标准授权</span>
                                    <span v-else class="grade-badge accent">需购买 ￥{{ item.grade_conf.price }}</span>
                                </div>
                            </div>
                            <div class="price-items">
                                <div v-for="(price, pIndex) in item.time_price" :key="pIndex" class="price-item">
                                    <span class="time-unit">{{ price.time_unit }}</span>
                                    <span class="price-amount">￥{{ price.price }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="pricing-note">
                        <warning-outlined />
                        不允许过多低于授权站普通用户标价授权，发现低价授权请举报，核实后将获得奖励。
                    </div>
                </div>
            </section>

            <!-- 代理服务 -->
            <section class="agent-service">
                <div class="agent-card">
                    <div class="agent-icon">
                        <span class="custom-icon">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21"
                                    stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round" />
                                <circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="2"
                                    stroke-linecap="round" stroke-linejoin="round" />
                                <path
                                    d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13"
                                    stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round" />
                                <path
                                    d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88"
                                    stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round" />
                            </svg>
                        </span>
                    </div>
                    <div class="agent-content">
                        <h3>多渠道支付方案</h3>
                        <p class="agent-desc">易售客服仅支持 <strong>USDT</strong>
                            付款，如果您想要<strong>微信、支付宝</strong>付款，请联系各大代理用户获取更多支付方式。</p>
                        <div class="agent-contacts" v-if="agentList.length > 0">
                            <div class="agent-contact-item" v-for="agent in agentList" :key="agent.uid">
                                <span class="agent-name">{{ agent.username }}</span>
                                <span class="agent-info" v-if="agent.contact">{{ agent.contact }}</span>
                            </div>
                        </div>
                        <a-button type="primary" class="agent-btn" @click="goToAgentPage">
                            <arrow-right-outlined />
                            查看更多代理用户
                        </a-button>
                    </div>
                </div>
            </section>

            <!-- Service -->
            <section id="service" class="service">
                <div class="service-head">
                    <span class="badge">优势与保障</span>
                    <h2>为什么选择我们</h2>
                    <p>专业授权平台，提供稳定可靠的技术支持与服务保障。</p>
                </div>
                <div class="service-grid">
                    <div class="service-card">
                        <div class="icon blue"><lock-outlined /></div>
                        <h3>安全可靠</h3>
                        <p>采用先进加密技术，保障数据与授权安全。</p>
                    </div>
                    <div class="service-card">
                        <div class="icon green"><sync-outlined /></div>
                        <h3>持续更新</h3>
                        <p>定期迭代功能，确保始终使用最新版本。</p>
                    </div>
                    <div class="service-card">
                        <div class="icon purple"><customer-service-outlined /></div>
                        <h3>专业支持</h3>
                        <p>7x24 技术支持，快速响应业务需求。</p>
                    </div>
                </div>
            </section>

            <!-- Process -->
            <section class="process">
                <div class="section-head">
                    <h2>服务流程</h2>
                    <p>从咨询到交付，全流程可视化服务。</p>
                </div>
                <div class="process-steps">
                    <div class="step">
                        <span class="step-index">01</span>
                        <h4>需求沟通</h4>
                        <p>梳理业务场景，确定版本与授权策略。</p>
                    </div>
                    <div class="step">
                        <span class="step-index">02</span>
                        <h4>方案配置</h4>
                        <p>多档位价格与授权时长配置，灵活匹配。</p>
                    </div>
                    <div class="step">
                        <span class="step-index">03</span>
                        <h4>上线交付</h4>
                        <p>秒级开通，授权即时生效。</p>
                    </div>
                    <div class="step">
                        <span class="step-index">04</span>
                        <h4>运营支持</h4>
                        <p>持续更新与售后支持，助力增长。</p>
                    </div>
                </div>
            </section>

            <!-- Trust -->
            <section class="trust">
                <div class="section-head">
                    <h2>合作与信任</h2>
                    <p>稳定、安全、专业，持续为合作伙伴创造价值。</p>
                </div>
                <div class="trust-grid">
                    <div class="trust-item">企业级 SLA 保障</div>
                    <div class="trust-item">多节点容灾</div>
                    <div class="trust-item">数据安全合规</div>
                    <div class="trust-item">专属顾问服务</div>
                    <div class="trust-item">自动化续费</div>
                    <div class="trust-item">全链路监控</div>
                </div>
            </section>
        </main>

        <IndexFooter class="footer" />
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import axios from 'axios';
import { message } from 'ant-design-vue'
import { getIncludesList } from '@/utils/publicMethods'
import { openLoading, fullscreenLoading } from '@/utils/loadingUtils'
import IndexHeader from '@/components/IndexHeader.vue'
import IndexFooter from '@/components/IndexFooter.vue'

const baseURL = process.env.VUE_APP_API_BASE_URL;

export default defineComponent({
    name: 'IndexView',
    components: {
        IndexHeader,
        IndexFooter
    },
    data() {
        return {
            featuredPlugin: null,
            featuredPluginPrice: null,
            activeFeaturedVersion: 0,
            featuredCurrentGrade: 0,
            isMobileMenuOpen: false,
            agentList: [],
        }
    },
    methods: {
        async getFeaturedPlugin() {
            try {
                const res_data = await getIncludesList(1, 1, 20);
                if (res_data.code === 0 && res_data.data.length > 0) {
                    this.featuredPlugin = res_data.data[0];
                    await this.getFeaturedPluginPrice(20);
                } else {
                    message.error(res_data.msg || '获取插件信息失败');
                }
            } catch (error) {
                console.error('Error fetching featured plugin:', error);
                message.error('获取插件信息失败');
            }
        },

        async getFeaturedPluginPrice(id) {
            openLoading();
            try {
                const headers = {
                    'token': localStorage.getItem("token")
                };
                const data = new FormData();
                data.append("includes_id", id.toString());

                const res = await axios.post(
                    baseURL + "/userApi/getGradeTimeMeal",
                    data,
                    { timeout: 30000, headers }
                );

                fullscreenLoading.close();

                if (res.data.code == 200) {
                    const versions_data = res.data.data.data;
                    this.featuredPluginPrice = {};
                    this.activeFeaturedVersion = 0;

                    versions_data.forEach((version_item, index) => {
                        const version_name = version_item.version_name || `版本 ${index + 1}`;
                        const version_key = index;

                        this.featuredPluginPrice[version_key] = {
                            name: version_name,
                            desc: version_item.version_desc,
                            meals: []
                        };

                        version_item.grade_arr.forEach(grade_item => {
                            this.featuredPluginPrice[version_key].meals.push({
                                grade_conf: grade_item.grade_conf,
                                time_price: grade_item.time_price.slice().sort((a, b) => {
                                    return a.meal_id - b.meal_id;
                                }),
                            });
                        });

                        this.featuredPluginPrice[version_key].meals.sort((a, b) => {
                            return a.grade_conf.grade - b.grade_conf.grade;
                        });
                    });

                    if (!res.data.data.current_grade) {
                        this.featuredCurrentGrade = 0;
                    } else {
                        this.featuredCurrentGrade = parseInt(res.data.data.current_grade);
                    }
                } else {
                    message.error(res.data.msg);
                }
            } catch (err) {
                fullscreenLoading.close();
                console.error(err);
                message.error('获取价格表失败：' + err.message);
            }
        },

        toggleMobileMenu() {
            this.isMobileMenuOpen = !this.isMobileMenuOpen;
        },

        formatDescription(text) {
            if (!text) return '';
            return text.replace(/\n/g, '<br/>');
        },

        openNotice() {
            window.dispatchEvent(new Event('show-notice-dialog'));
        },

        async getAgentList() {
            try {
                const res = await axios.get(baseURL + '/xiaochu/getAgentList?includes_id=1&page=1&pageSize=10', {
                    timeout: 30000
                });
                if (res.data.code === 200) {
                    this.agentList = res.data.data.list || [];
                }
            } catch (error) {
                console.error('获取代理列表失败:', error);
            }
        },

        goToAgentPage() {
            this.$router.push('/agentList');
        }
    },
    mounted() {
        this.getFeaturedPlugin();
        this.getAgentList();
    }
})
</script>

<style scoped lang="scss">
.page {
    background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 60%, #f8fafc 100%);
    min-height: 100vh;
}

.page-main {
    max-width: 1200px;
    margin: 0 auto;
    padding: 12vh 1.5rem 4rem;
    display: flex;
    flex-direction: column;
    gap: 3rem;
    height: 90vh;
    overflow-y: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
}

.page-main::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
}

.hero {
    position: relative;
    border-radius: 1.6rem;
    padding: 3rem 2.5rem;
    background: radial-gradient(circle at 10% 20%, rgba(59, 130, 246, 0.2), transparent 35%),
        radial-gradient(circle at 80% 0%, rgba(99, 102, 241, 0.18), transparent 40%),
        #ffffff;
    box-shadow: 0 18px 40px -24px rgba(37, 99, 235, 0.35);
    border: 1px solid rgba(226, 232, 240, 0.9);

    @media (max-width: 768px) {
        padding: 1.5rem;
    }
}

.hero-content {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 2.5rem;
    align-items: center;

    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
    }
}

.hero-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.35rem 0.9rem;
    border-radius: 9999px;
    background: rgba(37, 99, 235, 0.12);
    color: #1d4ed8;
    font-size: 0.85rem;
    font-weight: 600;

    @media (max-width: 768px) {
        padding: 0.25rem 0.6rem;
        font-size: 0.75rem;
    }
}

.hero-title {
    font-size: 2.6rem;
    font-weight: 800;
    color: #0f172a;
    margin: 0.6rem 0 0.4rem;

    @media (max-width: 768px) {
        font-size: 1.8rem;
        margin: 0.4rem 0 0.3rem;
    }
}

.hero-subtitle {
    color: #475569;
    line-height: 1.75;
    max-width: 32rem;

    @media (max-width: 768px) {
        font-size: 0.9rem;
        line-height: 1.5;
    }
}

.hero-actions {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    margin-top: 1rem;

    @media (max-width: 768px) {
        gap: 0.6rem;
        margin-top: 0.8rem;
    }
}

.btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.8rem 1.6rem;
    border-radius: 1rem;
    background: linear-gradient(135deg, #2563eb, #3b82f6);
    color: #fff;
    font-weight: 600;
    transition: all 0.25s ease;
    box-shadow: 0 16px 26px -18px rgba(37, 99, 235, 0.6);

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 20px 30px -18px rgba(37, 99, 235, 0.7);
    }

    @media (max-width: 768px) {
        padding: 0.6rem 1rem;
        font-size: 0.85rem;
        border-radius: 0.8rem;
    }
}

.btn-ghost {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.8rem 1.5rem;
    border-radius: 1rem;
    border: 1px solid rgba(148, 163, 184, 0.6);
    background: #ffffff;
    color: #1e293b;
    font-weight: 600;
    transition: all 0.25s ease;

    &:hover {
        transform: translateY(-2px);
        border-color: rgba(37, 99, 235, 0.5);
        box-shadow: 0 12px 22px -18px rgba(15, 23, 42, 0.35);
    }

    @media (max-width: 768px) {
        padding: 0.6rem 1rem;
        font-size: 0.85rem;
        border-radius: 0.8rem;
    }
}

.hero-kpis {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1rem;
    margin-top: 1.5rem;

    @media (max-width: 768px) {
        grid-template-columns: repeat(3, 1fr);
        gap: 0.5rem;
        margin-top: 1rem;
    }
}

.kpi {
    padding: 0.85rem 1rem;
    border-radius: 1rem;
    background: #f8fafc;
    border: 1px solid rgba(226, 232, 240, 0.9);
    box-shadow: 0 10px 18px -20px rgba(15, 23, 42, 0.3);

    @media (max-width: 768px) {
        padding: 0.5rem 0.6rem;
    }
}

.kpi-value {
    font-size: 1.2rem;
    font-weight: 700;
    color: #0f172a;

    @media (max-width: 768px) {
        font-size: 1rem;
    }
}

.kpi-label {
    font-size: 0.85rem;
    color: #64748b;
    margin-top: 0.2rem;

    @media (max-width: 768px) {
        font-size: 0.7rem;
    }
}

.hero-card {
    border-radius: 1.3rem;
    background: #ffffff;
    padding: 1.6rem;
    border: 1px solid rgba(226, 232, 240, 0.9);
    box-shadow: 0 18px 32px -24px rgba(15, 23, 42, 0.35);

    @media (max-width: 768px) {
        padding: 1rem;
        margin-top: 1rem;
    }
}

.hero-card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;

    h3 {
        font-size: 1.2rem;
        font-weight: 700;
        color: #0f172a;

        @media (max-width: 768px) {
            font-size: 1rem;
        }
    }
}

.hero-card-tag {
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.2rem 0.7rem;
    border-radius: 9999px;
    background: rgba(37, 99, 235, 0.12);
    color: #1e40af;

    @media (max-width: 768px) {
        font-size: 0.65rem;
        padding: 0.15rem 0.5rem;
    }
}

.hero-list {
    display: grid;
    gap: 0.7rem;
    color: #475569;
    font-size: 0.95rem;

    @media (max-width: 768px) {
        gap: 0.5rem;
        font-size: 0.85rem;
    }

    .anticon {
        color: #2563eb;
        margin-right: 0.5rem;
    }
}

.hero-card-footer {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    font-size: 0.8rem;
    color: #64748b;
    margin-top: 1.5rem;

    @media (max-width: 768px) {
        margin-top: 1rem;
        font-size: 0.7rem;
    }

    .dot {
        width: 4px;
        height: 4px;
        border-radius: 9999px;
        background: #94a3b8;
    }
}

.pricing {
    background: #ffffff;
    border-radius: 1.6rem;
    padding: 2.5rem;
    border: 1px solid rgba(226, 232, 240, 0.9);
    box-shadow: 0 20px 36px -26px rgba(15, 23, 42, 0.35);
}

.pricing-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1.5rem;
    flex-wrap: wrap;
    margin-bottom: 2rem;
}

.section-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #0f172a;
}

.section-desc {
    color: #64748b;
    margin-top: 0.4rem;
}

.pricing-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
    color: #64748b;
    font-size: 0.85rem;

    span {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        background: #f8fafc;
        padding: 0.3rem 0.7rem;
        border-radius: 9999px;
        border: 1px solid rgba(226, 232, 240, 0.9);
    }
}

.pricing-body {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.version-tabs {
    :deep(.ant-tabs-nav) {
        margin: 0;
    }

    :deep(.ant-tabs-tab) {
        font-weight: 600;
        color: #6b7280;
    }

    :deep(.ant-tabs-tab.ant-tabs-tab-active) {
        color: #2563eb;
    }

    :deep(.ant-tabs-content-holder) {
        display: none;
    }
}

.version-desc {
    display: flex;
    gap: 0.6rem;
    align-items: flex-start;
    padding: 0.9rem 1rem;
    border-radius: 0.9rem;
    background: #eff6ff;
    color: #1e3a8a;
    border: 1px solid rgba(59, 130, 246, 0.2);

    p {
        margin: 0;
        font-size: 0.9rem;
        line-height: 1.6;
    }
}

.price-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 1.2rem;
}

.price-card {
    background: #ffffff;
    border-radius: 1rem;
    border: 1px solid rgba(226, 232, 240, 0.9);
    box-shadow: 0 12px 22px -20px rgba(15, 23, 42, 0.35);
    overflow: hidden;
}

.price-card-head {
    padding: 1rem 1.2rem;
    background: #f8fafc;

    &.accent {
        background: rgba(37, 99, 235, 0.1);
    }
}

.price-title {
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
    align-items: center;
}

.grade-name {
    font-weight: 700;
    color: #0f172a;
}

.grade-badge {
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0.2rem 0.6rem;
    border-radius: 9999px;
    background: #e2e8f0;
    color: #475569;
}

.grade-badge.accent {
    background: rgba(37, 99, 235, 0.16);
    color: #1e40af;
}

.price-items {
    padding: 1rem;
    display: grid;
    gap: 0.6rem;
}

.price-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.6rem 0.8rem;
    border-radius: 0.7rem;
    background: #f8fafc;
    border: 1px solid rgba(226, 232, 240, 0.9);
}

.time-unit {
    color: #64748b;
    font-size: 0.85rem;
}

.price-amount {
    font-weight: 700;
    color: #2563eb;
}

.pricing-note {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.7rem 1rem;
    border-radius: 0.9rem;
    background: #fff7ed;
    color: #9a3412;
    border: 1px solid rgba(251, 146, 60, 0.3);
    font-size: 0.9rem;
}

.service {
    background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%);
    border-radius: 1.6rem;
    padding: 2.5rem;
    border: 1px solid rgba(226, 232, 240, 0.9);
    box-shadow: 0 18px 30px -24px rgba(15, 23, 42, 0.35);
}

.service-head {
    text-align: center;
    margin-bottom: 2rem;

    h2 {
        font-size: 1.7rem;
        font-weight: 700;
        color: #0f172a;
        margin: 0.4rem 0;
    }

    p {
        color: #64748b;
    }
}

.badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.3rem 0.8rem;
    border-radius: 9999px;
    background: rgba(37, 99, 235, 0.12);
    color: #1e40af;
    font-size: 0.8rem;
    font-weight: 600;
}

.service-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1.2rem;
}

.service-card {
    background: #ffffff;
    border-radius: 1.1rem;
    padding: 1.5rem;
    border: 1px solid rgba(226, 232, 240, 0.9);
    box-shadow: 0 12px 22px -20px rgba(15, 23, 42, 0.3);

    h3 {
        margin: 0.6rem 0 0.3rem;
        font-weight: 600;
        color: #0f172a;
    }

    p {
        color: #64748b;
        font-size: 0.95rem;
    }
}

.icon {
    width: 3rem;
    height: 3rem;
    border-radius: 0.9rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.4rem;
    border: 1px solid rgba(226, 232, 240, 0.9);
    background: #f8fafc;
}

.icon.blue {
    color: #2563eb;
    background: rgba(37, 99, 235, 0.12);
}

.icon.green {
    color: #16a34a;
    background: rgba(34, 197, 94, 0.12);
}

.icon.purple {
    color: #7c3aed;
    background: rgba(124, 58, 237, 0.12);
}

.process,
.trust {
    background: #ffffff;
    border-radius: 1.5rem;
    padding: 2.4rem 2rem;
    border: 1px solid rgba(226, 232, 240, 0.85);
    box-shadow: 0 18px 28px -24px rgba(15, 23, 42, 0.3);
}

.section-head {
    text-align: center;
    margin-bottom: 1.8rem;

    h2 {
        font-size: 1.6rem;
        font-weight: 700;
        color: #0f172a;
        margin-bottom: 0.4rem;
    }

    p {
        color: #64748b;
    }
}

.process-steps {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1.2rem;
}

.step {
    background: #f8fafc;
    border-radius: 1.1rem;
    padding: 1.4rem;
    border: 1px solid rgba(226, 232, 240, 0.9);
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0 14px 24px -22px rgba(37, 99, 235, 0.35);
    }

    h4 {
        font-weight: 600;
        color: #0f172a;
        margin: 0.5rem 0 0.4rem;
    }

    p {
        color: #64748b;
        font-size: 0.95rem;
    }
}

.step-index {
    font-size: 0.8rem;
    font-weight: 700;
    color: #2563eb;
    background: rgba(59, 130, 246, 0.1);
    padding: 0.2rem 0.6rem;
    border-radius: 9999px;
}

.trust-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 1rem;
}

.trust-item {
    background: #f8fafc;
    border-radius: 0.9rem;
    padding: 1rem;
    text-align: center;
    font-weight: 600;
    color: #334155;
    border: 1px dashed rgba(148, 163, 184, 0.6);
    box-shadow: 0 10px 18px -20px rgba(15, 23, 42, 0.25);
}

.agent-service {
    width: 100%;
}

.agent-card {
    display: flex;
    gap: 1.5rem;
    background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 50%, #f0fdfa 100%);
    border-radius: 1.4rem;
    padding: 2rem;
    border: 1px solid rgba(34, 197, 94, 0.2);
    box-shadow: 0 20px 36px -26px rgba(34, 197, 94, 0.25);

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 1rem;
        padding: 1.5rem;
    }
}

.agent-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    min-width: 64px;
    background: linear-gradient(135deg, #22c55e, #16a34a);
    border-radius: 1.2rem;
    box-shadow: 0 10px 20px -10px rgba(34, 197, 94, 0.5);
    border: 2px solid rgba(255, 255, 255, 0.3);

    @media (max-width: 768px) {
        width: 48px;
        height: 48px;
        min-width: 48px;
    }

    .custom-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        color: #ffffff;
        filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));

        svg {
            width: 100%;
            height: 100%;
        }

        @media (max-width: 768px) {
            width: 24px;
            height: 24px;
        }
    }
}

.agent-content {
    flex: 1;

    h3 {
        font-size: 1.3rem;
        font-weight: 700;
        color: #0f172a;
        margin: 0 0 0.5rem;
    }
}

.agent-desc {
    color: #475569;
    line-height: 1.6;
    margin: 0 0 1rem;

    strong {
        color: #15803d;
        font-weight: 600;
    }
}

.agent-contacts {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-bottom: 1.2rem;
}

.agent-contact-item {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    background: #fff;
    padding: 0.75rem 1rem;
    border-radius: 0.8rem;
    border: 1px solid rgba(34, 197, 94, 0.15);
    box-shadow: 0 4px 12px -4px rgba(0, 0, 0, 0.1);
}

.agent-name {
    font-weight: 600;
    color: #15803d;
    font-size: 0.95rem;
}

.agent-info {
    font-size: 0.8rem;
    color: #64748b;
}

.agent-btn {
    background: linear-gradient(135deg, #22c55e, #16a34a);
    border-color: transparent;
    color: #fff;
    padding: 0.8rem 1.5rem;
    border-radius: 0.8rem;
    font-weight: 600;
    transition: all 0.25s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 20px -8px rgba(34, 197, 94, 0.5);
    }

    .anticon {
        margin-right: 0.3rem;
    }
}

.footer {
    margin-top: 2rem;
}
</style>
